import { defineComponent as _defineComponent } from 'vue'
import { PropType, onMounted, ref, computed } from 'vue';

import { FormItem } from '@/interfaces/lib/model';
import {
  SdsSection3ImpurityCas,
  SdsSection3ImpurityMaterial,
  SdsSection3Material,
} from '@/interfaces/model/general/sds';
import { useParentImpurities } from '@/composables/useParentImpurities';
import { generateTempImpurityId } from '@/utils/uuid';
import {
  getConcentrationInequalityList,
  concentrationUnitList,
  textFormList,
} from '@/lib/formItem';

import {
  isFloat,
  isNumberAndAlphabetsAndHyphenArray,
  required,
  requiredSelection,
} from '@/lib/rule';
import { VForm } from 'vuetify/lib/components';
import { i18n } from '@/plugins/vue-i18n';
import sharedMessages from '@/lib/shared-messages';


export default /*#__PURE__*/_defineComponent({
  __name: 'SdsSection3ImpurityForm',
  props: {
  isEditing: {
    type: Boolean,
    required: true,
  },
  isValid: {
    type: Boolean,
    required: true,
  },
  sdsId: {
    type: String,
    required: true,
  },
  sdsSection3ImpurityMaterialId: {
    type: Number,
    required: true,
  },
  materialName: {
    type: String,
    required: true,
  },
  casList: {
    type: Array as PropType<Array<SdsSection3ImpurityCas>>,
    required: true,
  },
  encsNumber: {
    type: String,
    required: true,
  },
  ishlNumber: {
    type: String,
    required: true,
  },
  isStated: {
    type: Boolean,
    required: true,
  },
  concentration: {
    type: String,
    required: true,
  },
  concentrationMin: {
    type: Number,
    required: false,
    default: null,
  },
  concentrationMax: {
    type: Number,
    required: false,
    default: null,
  },
  concentrationInequalitySign: {
    type: Number,
    required: true,
  },
  concentrationUnit: {
    type: String,
    required: true,
  },
  // Parent impurity props
  parentImpurityMaterialId: {
    type: Number,
    default: null,
  },
  tempImpurityMaterialId: {
    type: String,
    default: undefined,
  },
  tempParentImpurityMaterialId: {
    type: String,
    default: null,
  },
  allImpurities: {
    type: Array as PropType<SdsSection3ImpurityMaterial[]>,
    required: true,
  },
  allMaterials: {
    type: Array as PropType<SdsSection3Material[]>,
    required: true,
  },
},
  emits: ["update:isValid", "update:materialName", "update:casList", "update:encsNumber", "update:ishlNumber", "update:isStated", "update:concentrationInequalitySign", "update:concentrationMin", "update:concentrationMax", "update:concentrationUnit", "update:isImpurityConcentrationSecret", "update:parentImpurityMaterialId", "update:tempImpurityMaterialId", "update:tempParentImpurityMaterialId"],
  setup(__props, { emit: emits }: { emit: ({
  (e: 'update:isValid', data: boolean): void;
  (e: 'update:materialName', data: string): void;
  (e: 'update:casList', data: Array<SdsSection3ImpurityCas>): void;
  (e: 'update:encsNumber', data: string): void;
  (e: 'update:ishlNumber', data: string): void;
  (e: 'update:isStated', data: boolean): void;
  (e: 'update:concentrationInequalitySign', data: number): void;
  (e: 'update:concentrationMin', data: number | null): void;
  (e: 'update:concentrationMax', data: number | null): void;
  (e: 'update:concentrationUnit', data: string): void;
  (e: 'update:isImpurityConcentrationSecret', data: boolean): void;
  // Parent impurity emits
  (e: 'update:parentImpurityMaterialId', data: number | null): void;
  (e: 'update:tempImpurityMaterialId', data: string): void;
  (e: 'update:tempParentImpurityMaterialId', data: string | null): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;

const $t = i18n.t.bind(i18n);



const isImpurityConcentrationSecret = ref<boolean>(false);
const isNotStated = ref<boolean>(!props.isStated);
const form = ref<InstanceType<typeof VForm> | null>(null);



// Parent impurities composable
const impuritiesRef = computed(() => props.allImpurities);
const materialsRef = computed(() => props.allMaterials);
const { getAvailableParents } = useParentImpurities(
  impuritiesRef,
  materialsRef
);

// Parent impurity reactive state
const hasParentImpurity = ref<boolean>(false);
const selectedParentId = ref<number | string | null>(null);

// Available parents for dropdown
const availableParents = computed(() => {
  const currentImpurity: SdsSection3ImpurityMaterial = {
    id: props.sdsSection3ImpurityMaterialId || 0,
    temp_impurity_material_id: props.tempImpurityMaterialId,
    parent_impurity_material_id: props.parentImpurityMaterialId,
    temp_parent_impurity_material_id: props.tempParentImpurityMaterialId,
    sds_id: props.sdsId,
    sds_section3_id: 0, // Will be set by parent component
    material_name: props.materialName,
    concentration: '',
    encs_number: props.encsNumber,
    ishl_number: props.ishlNumber,
    is_stated: props.isStated,
    concentration_inequality_sign: props.concentrationInequalitySign,
    concentration_min: props.concentrationMin,
    concentration_max: props.concentrationMax,
    concentration_unit: props.concentrationUnit,
    cas_list: props.casList,
    created_at: '',
    updated_at: '',
    deleted_at: null,
  };
  return getAvailableParents(currentImpurity);
});

// Helper function for v-select item-value
const getParentItemValue = (item: {
  id?: number;
  temp_impurity_material_id?: string;
}) => {
  return item.id || item.temp_impurity_material_id;
};

// Form event handlers
const onParentCheckboxChange = (checked: boolean) => {
  if (!checked) {
    selectedParentId.value = null;
    emits('update:parentImpurityMaterialId', null);
    emits('update:tempParentImpurityMaterialId', null);
  }
};

const onParentSelectionChange = (value: number | string | null) => {
  if (typeof value === 'number') {
    // Existing parent impurity
    emits('update:parentImpurityMaterialId', value);
    emits('update:tempParentImpurityMaterialId', null);
  } else if (typeof value === 'string') {
    // New parent impurity (temp UUID)
    emits('update:parentImpurityMaterialId', null);
    emits('update:tempParentImpurityMaterialId', value);
  } else {
    // Cleared selection
    emits('update:parentImpurityMaterialId', null);
    emits('update:tempParentImpurityMaterialId', null);
  }
};

const bindCas = (casList: Array<SdsSection3ImpurityCas>): Array<string> => {
  return casList.map((x: SdsSection3ImpurityCas) => {
    return x.cas_number;
  });
};

const showInequalityMin = (concentrationInequalitySign: number): boolean => {
  switch (concentrationInequalitySign) {
    case 1:
      return true;
    case 2:
      return true;
    case 3:
      return true;
    case 4:
      return false;
    case 5:
      return false;
    case 6:
      return true;
    case 7:
      return true;
    case 8:
      return true;
    case 9:
      return true;
    default:
      return false;
  }
};

const showInequalityMax = (concentrationInequalitySign: number): boolean => {
  switch (concentrationInequalitySign) {
    case 1:
      return false;
    case 2:
      return false;
    case 3:
      return false;
    case 4:
      return true;
    case 5:
      return true;
    case 6:
      return true;
    case 7:
      return true;
    case 8:
      return true;
    case 9:
      return true;
    default:
      return false;
  }
};

const inequalityMin = (concentrationInequalitySign: number): string => {
  switch (concentrationInequalitySign) {
    case 1:
      return $t(sharedMessages.equal);
    case 2:
      return $t(sharedMessages.greaterThanOrEqual);
    case 3:
      return $t(sharedMessages.greaterThan);
    case 4:
      return '';
    case 5:
      return '';
    case 6:
      return $t(sharedMessages.greaterThanOrEqual);
    case 7:
      return $t(sharedMessages.greaterThanOrEqual);
    case 8:
      return $t(sharedMessages.greaterThan);
    case 9:
      return $t(sharedMessages.greaterThan);
    default:
      return '';
  }
};

const inequalityMax = (concentrationInequalitySign: number): string => {
  switch (concentrationInequalitySign) {
    case 1:
      return '';
    case 2:
      return '';
    case 3:
      return '';
    case 4:
      return $t(sharedMessages.lessThanOrEqual);
    case 5:
      return $t(sharedMessages.lessThan);
    case 6:
      return $t(sharedMessages.lessThanOrEqual);
    case 7:
      return $t(sharedMessages.lessThan);
    case 8:
      return $t(sharedMessages.lessThanOrEqual);
    case 9:
      return $t(sharedMessages.lessThan);
    default:
      return '';
  }
};

const encsNumberValue = (encsNumber: string): Array<string> => {
  return encsNumber.split('|').filter((x) => x != '');
};

const ishlNumberValue = (ishlNumber: string): Array<string> => {
  return ishlNumber.split('|').filter((x) => x != '');
};

const updateIsValid = (e: boolean): void => {
  emits('update:isValid', e);
};

const updateIsStated = (e: boolean): void => {
  emits('update:isStated', !e);
};

const updateMaterialName = (e: string): void => {
  emits('update:materialName', e);
};

const updateEncsNumber = (e: Array<string | FormItem>): void => {
  const encsList = e.map((item) => {
    if (typeof item == 'string') {
      return item;
    } else {
      return String(item.value);
    }
  });

  emits('update:encsNumber', encsList.join('|'));
};

const updateIshlNumber = (e: Array<string | FormItem>): void => {
  const ishlList = e.map((item) => {
    if (typeof item == 'string') {
      return item;
    } else {
      return String(item.value);
    }
  });

  emits('update:ishlNumber', ishlList.join('|'));
};

const updateConcentrationMin = (e: string | null): void => {
  let concentrationMin: number | null = null;
  if (e) {
    concentrationMin = Number(e);
  }
  emits('update:concentrationMin', concentrationMin);
};

const updateConcentrationMax = (e: string | null): void => {
  let concentrationMax: number | null = null;
  if (e) {
    concentrationMax = Number(e);
  }
  emits('update:concentrationMax', concentrationMax);
};

const updateConcentrationUnit = (e: null | string | FormItem): void => {
  emits('update:concentrationUnit', e?.value ?? e ?? '');
};

const updateConcentrationInequalitySign = (e: number): void => {
  emits('update:concentrationInequalitySign', e);
};

const updateCas = (e: Array<string | FormItem>) => {
  const casList: Array<SdsSection3ImpurityCas> = e.map(
    (i: string | FormItem) => {
      return {
        id: 0,
        cas_number: i?.value ?? i,
        sds_id: props.sdsId,
        sds_section3_impurity_material_id: props.sdsSection3ImpurityMaterialId,
        created_at: '',
        updated_at: '',
        deleted_at: null,
      };
    }
  );

  emits('update:casList', casList);
};

const updateIsImpurityConcentrationSecret = (e: boolean): void => {
  if (e) {
    updateConcentrationMin(null);
    updateConcentrationMax(null);
    updateConcentrationUnit('');
    updateConcentrationInequalitySign(0);
  } else {
    updateConcentrationMin(0);
    updateConcentrationMax(0);
    updateConcentrationUnit('%');
    updateConcentrationInequalitySign(1);
  }
};

onMounted((): void => {
  if (form.value) {
    form.value.validate();
  }
  if (props.concentrationInequalitySign == 0) {
    isImpurityConcentrationSecret.value = true;
  } else {
    isImpurityConcentrationSecret.value = false;
  }

  // Initialize parent impurity state
  // Generate temp_impurity_material_id for new impurities if not provided
  if (
    !props.tempImpurityMaterialId &&
    (!props.sdsSection3ImpurityMaterialId ||
      props.sdsSection3ImpurityMaterialId === 0)
  ) {
    emits('update:tempImpurityMaterialId', generateTempImpurityId());
  }

  // Initialize parent impurity selection state
  const hasParent = !!(
    props.parentImpurityMaterialId || props.tempParentImpurityMaterialId
  );
  hasParentImpurity.value = hasParent;
  selectedParentId.value =
    props.parentImpurityMaterialId ||
    props.tempParentImpurityMaterialId ||
    null;
});

return { __sfc: true,$t, props, isImpurityConcentrationSecret, isNotStated, form, emits, impuritiesRef, materialsRef, getAvailableParents, hasParentImpurity, selectedParentId, availableParents, getParentItemValue, onParentCheckboxChange, onParentSelectionChange, bindCas, showInequalityMin, showInequalityMax, inequalityMin, inequalityMax, encsNumberValue, ishlNumberValue, updateIsValid, updateIsStated, updateMaterialName, updateEncsNumber, updateIshlNumber, updateConcentrationMin, updateConcentrationMax, updateConcentrationUnit, updateConcentrationInequalitySign, updateCas, updateIsImpurityConcentrationSecret, getConcentrationInequalityList, concentrationUnitList, textFormList, isFloat, isNumberAndAlphabetsAndHyphenArray, required, requiredSelection, VForm, sharedMessages }
}

})